import classNames from "classnames";
import L from "leaflet";
import { useContext, useEffect, useRef, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";

import FilterDropdown from "src/app/components/filterDropdown/FilterDropdown";
import Loader from "src/app/components/loader/Loader";
import { ProjectSystem, ProjectSystemAdditionalParameter, SYSTEM_PARAMETER_TYPE } from "src/app/modules/calculator/types";
import activePin from "src/assets/iconsGraphics/pin-active.png";
import pin from "src/assets/iconsGraphics/pin.png";
import { ModalContext } from "src/contexts/ModalContext";
import { ApiResult } from "src/core/helpers/api/ApiResult";
import { PartnerResponse } from "src/core/models/partners/PartnerResponse";
import { ProjectRequest } from "src/core/models/projects/ProjectRequest";
import { PartnersService } from "src/core/services/partners";
import { ProjectsService } from "src/core/services/projects";

import modalCSS from "./PartnerModal.module.css";

const defaultLat = 45;
const defaultLon = 16.3;
type IFormInputs = {
  projectName: string;
};

function PartnerModal({
  selectedSystems,
  handleProjectSent,
  handlePartnerSelected,
  hideModal,
}: {
  selectedSystems: Array<ProjectSystem>;
  handleProjectSent: (isSent: boolean) => void;
  handlePartnerSelected: (selectedPartner: PartnerResponse | undefined) => void;
  hideModal: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [sendLoading, setSendLoading] = useState(false);
  const [partners, setPartners] = useState<Array<PartnerResponse>>([]);
  const [partnersSelectList, setPartnersSelectList] = useState<Array<any>>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormInputs>();

  const buildAdditionalParameters = (projectSystem: ProjectSystem) => {
    let res = Array<ProjectSystemAdditionalParameter>();
    if (projectSystem.requiredApplianceThickness > 0) {
      res.push({
        systemAdditionalParameterId: projectSystem?.system?.additionalParameters?.find((y) => y.parameterType === SYSTEM_PARAMETER_TYPE.ApplianceThickness)?.id,
        value: projectSystem.requiredApplianceThickness,
      });
    }
    return res;
  };

  const [currentState, setFormData] = useState<ProjectRequest>({
    projectName: "",
    partnerId: 0,
    projectSystems: selectedSystems.map((x: ProjectSystem) => ({
      ...x,
      additionalParameters: buildAdditionalParameters(x),
    })),
    message: "Poštovani, molimo vas ponudu prema kalkulaciji utroška materijala.",
  });
  const modalData = useContext(ModalContext);

  useEffect(() => {
    if (loading) {
      PartnersService.get()
        .then((response: any) => {
          if (response.records) {
            const filteredPartners = response.records
              .filter((partner: PartnerResponse) => {
                return partner.locationX && partner.locationY;
              })
              .sort((a: PartnerResponse, b: PartnerResponse) => (a.zipCode && b.zipCode && a.zipCode > b.zipCode ? 1 : -1));
            setPartners(filteredPartners);
            setPartnersSelectList(
              filteredPartners.map((partner: PartnerResponse) => {
                let value = partner.id;
                let text = `${partner.name}, ${partner.zipCode} - ${partner.address}`;
                return { value: value, text: text };
              })
            );
            setFormData((currentState) => {
              return {
                ...currentState,
                partnerId: filteredPartners[0].id,
              };
            });
            handlePartnerSelected(filteredPartners[0]);
          }
        })
        .catch((e: ApiResult) => {
          if (!modalData?.content?.contentSet) {
            modalData?.triggerError(e);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [loading, modalData, handlePartnerSelected]);

  function handlePartnerSelect(partner: PartnerResponse) {
    setFormData((currentState) => {
      return {
        ...currentState,
        partnerId: partner.id,
      };
    });
  }

  const onInputChange: React.ChangeEventHandler<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = (event) => {
    const inputName = event.target.name;
    const inputValue = event.target.value;
    setFormData((currentState) => {
      return {
        ...currentState,
        [inputName]: inputValue,
      };
    });
  };

  const onPartnerChange = (partnerId: number) => {
    setFormData((currentState) => {
      return {
        ...currentState,
        partnerId: partnerId,
      };
    });
  };

  const handleFormSubmit: SubmitHandler<IFormInputs> = (data) => {
    setSendLoading(true);
    ProjectsService.add({
      projectName: data.projectName,
      partnerId: currentState.partnerId,
      projectSystems: currentState.projectSystems,
      message: currentState.message,
    })
      .then((response: any) => {
        modalData?.triggerSuccess(response);
        handleProjectSent(true);
        const selectedPartner = partners.find((partner) => partner.id === Number(currentState.partnerId ?? 0));
        handlePartnerSelected(selectedPartner);
        hideModal();
      })
      .catch((e: ApiResult) => {
        modalData?.triggerError(e);
        handleProjectSent(false);
      })
      .finally(() => {
        setSendLoading(false);
      });
  };

  useEffect(() => {
    if (!sendLoading) {
      if (!currentState.partnerId && partners?.length > 0 && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(successCallback);
      }
    }

    function successCallback(position: any) {
      handlePartnerSelect(findNearestPartner(position.coords.latitude, position.coords.longitude));
    }

    function findNearestPartner(latitude: number, longitude: number) {
      var minDif = 99999;
      var closest = 0;

      for (let index = 0; index < partners.length; ++index) {
        var dif = PythagorasEquirectangular(latitude, longitude, Number(partners[index].locationX), Number(partners[index].locationY));
        if (dif < minDif) {
          closest = index;
          minDif = dif;
        }
      }

      // echo the nearest
      return partners[closest];
    }

    // Convert Degress to Radians
    function Deg2Rad(deg: number) {
      return (deg * Math.PI) / 180;
    }

    function PythagorasEquirectangular(lat1: number, lon1: number, lat2: number, lon2: number) {
      if (lat1 && lon1 && lat2 && lon2) {
        lat1 = Deg2Rad(lat1);
        lat2 = Deg2Rad(lat2);
        lon1 = Deg2Rad(lon1);
        lon2 = Deg2Rad(lon2);
        var R = 6371; // km
        var x = (lon2 - lon1) * Math.cos((lat1 + lat2) / 2);
        var y = lat2 - lat1;
        var d = Math.sqrt(x * x + y * y) * R;
        return d;
      } else {
        return Number(Math.max);
      }
    }
  }, [currentState, partners, sendLoading]);

  return (
    <div className={classNames("u-block", "u-pos-fixed", "u-z-index10", "u-w100", "u-h100 ", "u-bg-black", "u-bg-black-light", modalCSS.modal)}>
      <div className={classNames("u-pos-absolute", "u-flex", "u-col-lg-8", "u-col-sm-11", modalCSS.modalContent)}>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="u-flex u-col-xs-12">
          {/* Tekst lijevo */}
          <div className={classNames("u-col-lg-6", "u-col-sm-6", "u-col-xs-12", modalCSS.detailsContainer)}>
            <div>
              <h3 className="u-m0 u-mb30px u-t-uppercase u-t-primary-dark u-t-align-center">pošaljite izračun našim partnerima i zatražite ponudu</h3>
            </div>
            <div className={classNames("u-flex", "u-flex-column", "u-pb50px", modalCSS.inputs)}>
              <label htmlFor="projectName" className="u-t-f14p u-ml10px">
                Ime projekta
              </label>
              <input id="projectName" type="text" className="u-t-f15p u-mb30px" {...register("projectName", { required: true })}></input>
              {errors.projectName && <span className={classNames("u-t-f14p", modalCSS.errorMessage)}>Polje je obavezno</span>}
              <label className="u-t-f14p u-ml10px" htmlFor="partnerId">
                Odaberite partnera
              </label>
              <FilterDropdown
                items={partnersSelectList}
                activeItemValue={currentState.partnerId}
                defaultItem={{ value: 0, text: "svi partneri" }}
                handleChange={(partnerId: number) => {
                  onPartnerChange(partnerId);
                }}
                customCss={"u-t-f15p u-mb30px"}
              />
              <label className="u-t-f14p u-ml10px" htmlFor="message">
                Vaša poruka
              </label>
              <textarea
                value={currentState.message ?? ""}
                defaultValue={currentState.message ?? ""}
                onChange={onInputChange}
                name="message"
                id="message"
                rows={5}
                cols={50}
                className="u-t-f15p"
              ></textarea>
            </div>
            <div className={classNames("u-mt50px", modalCSS.desktopBtn)}>
              <button
                type="button"
                onClick={() => (sendLoading ? {} : hideModal())}
                className={classNames("u-t-uppercase", loading ? modalCSS.btnDisabled : "u-cursor-pointer", modalCSS.giveupBtn)}
              >
                <h6 className="u-m0 u-t-primary u-t-w400 u-t-f16p">odustani</h6>
              </button>
              <button
                type={sendLoading ? "button" : "submit"}
                className={classNames("u-t-uppercase", loading ? modalCSS.btnDisabled : "u-cursor-pointer", modalCSS.sendBtn)}
              >
                <h6 className="u-m0 u-t-white u-t-w400 u-t-f16p">pošalji izračun</h6>
              </button>
            </div>
          </div>
          {/* Mapa desno */}
          <div className={classNames("u-col-lg-6", "u-col-sm-6", "u-col-xs-12", modalCSS.mapContainer)}>
            <MapContainer key={currentState.partnerId ? currentState.partnerId : 0} center={[defaultLat, defaultLon]} zoom={7} scrollWheelZoom={false}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {partners?.length > 0 && (
                <Markers
                  data={partners}
                  selectedPartnerId={currentState.partnerId}
                  handlePartnerSelect={(partner: PartnerResponse) => handlePartnerSelect(partner)}
                />
              )}
            </MapContainer>
          </div>
          <div className={classNames("u-flex-column", "u-flex-items-center", modalCSS.mobileBtn)}>
            <button
              type="button"
              onClick={() => (sendLoading ? {} : hideModal())}
              className={classNames("u-t-uppercase", loading ? modalCSS.btnDisabled : "u-cursor-pointer", modalCSS.giveupBtn)}
            >
              <h6 className="u-m0 u-t-primary u-t-w400">odustani</h6>
            </button>
            <button
              type={sendLoading ? "button" : "submit"}
              className={classNames("u-t-uppercase", loading ? modalCSS.btnDisabled : "u-cursor-pointer", modalCSS.sendBtn)}
            >
              <h6 className="u-m0 u-t-white u-t-w400">pošalji izračun</h6>
            </button>
          </div>
        </form>
      </div>
      {(loading || sendLoading) && <Loader />}
    </div>
  );
}

export default PartnerModal;

const Markers: Function = ({
  data,
  selectedPartnerId,
  handlePartnerSelect,
}: {
  data: PartnerResponse[];
  selectedPartnerId: number;
  handlePartnerSelect: (partner: PartnerResponse) => void;
}): JSX.Element[] => {
  return data.map((partner: any) => {
    return (
      <PinMarker
        partner={partner}
        openPopup={partner.id === Number(selectedPartnerId)}
        handlePartnerSelect={(partner: PartnerResponse) => handlePartnerSelect(partner)}
      />
    );
  });
};

const PinMarker = ({
  partner,
  openPopup,
  handlePartnerSelect,
}: {
  partner: PartnerResponse;
  openPopup: boolean;
  handlePartnerSelect: (partner: PartnerResponse) => void;
}) => {
  const markerRef = useRef<any>(null);
  const map = useMap();

  const mapPin = new L.Icon({
    iconUrl: pin,
    iconSize: [24, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40],
  });
  const activeMapPin = new L.Icon({
    iconUrl: activePin,
    iconSize: [24, 30],
    iconAnchor: [10, 41],
    popupAnchor: [2, -40],
  });

  useEffect(() => {
    if (partner.locationX && partner.locationY && openPopup && markerRef && markerRef.current) {
      map.setView([partner.locationX, partner.locationY], 14);
      markerRef.current.openPopup();
    }
  }, [map, partner, openPopup]);

  return (
    <Marker
      ref={markerRef}
      position={[Number(partner.locationX), Number(partner.locationY)]}
      icon={openPopup ? activeMapPin : mapPin}
      key={partner.id}
      eventHandlers={{
        click: () => {
          handlePartnerSelect(partner);
        },
      }}
    >
      <Popup>
        {partner.name}, {partner.address}, {partner.zipCode}
      </Popup>
    </Marker>
  );
};
